// Custom Process Flow Edge Component
// Renders different connection types with appropriate styling

import React from 'react';
import {
    EdgeProps,
    getBezierPath,
    EdgeLabelRenderer,
    BaseEdge
} from 'reactflow';
import { ProcessEdgeData, ConnectionType, CONNECTION_COLORS } from '../../types/processFlow';

const ProcessFlowEdge: React.FC<EdgeProps<ProcessEdgeData>> = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    data,
    selected,
    markerEnd: _markerEnd,
}) => {
    const connectionType = data?.connectionType || ConnectionType.DEFAULT;
    const color = CONNECTION_COLORS[connectionType];

    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    });

    // Stroke style based on connection type
    const getStrokeStyle = () => {
        switch (connectionType) {
            case ConnectionType.REWORK:
                return { strokeDasharray: '8 4' };
            case ConnectionType.REJECT:
                return { strokeDasharray: '4 2' };
            case ConnectionType.INFORMATION:
                return { strokeDasharray: '2 2' };
            case ConnectionType.ALTERNATE:
                return { strokeDasharray: '12 4' };
            default:
                return {};
        }
    };

    // Arrow markers based on type
    const getMarkerUrl = () => {
        return `url(#arrow-${connectionType})`;
    };

    return (
        <>
            {/* Define arrow markers for each connection type */}
            <defs>
                {Object.entries(CONNECTION_COLORS).map(([type, color]) => (
                    <marker
                        key={type}
                        id={`arrow-${type}`}
                        markerWidth="12"
                        markerHeight="12"
                        refX="10"
                        refY="6"
                        orient="auto"
                    >
                        <path
                            d="M2,2 L10,6 L2,10 L4,6 Z"
                            fill={color}
                        />
                    </marker>
                ))}
            </defs>

            <BaseEdge
                id={id}
                path={edgePath}
                style={{
                    stroke: color,
                    strokeWidth: selected ? 3 : 2,
                    ...getStrokeStyle(),
                }}
                markerEnd={getMarkerUrl()}
            />

            {/* Edge label */}
            {data?.label && (
                <EdgeLabelRenderer>
                    <div
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                            pointerEvents: 'all',
                        }}
                        className="text-xs px-2 py-1 rounded bg-white shadow border border-slate-200 font-medium"
                    >
                        {data.label}
                    </div>
                </EdgeLabelRenderer>
            )}
        </>
    );
};

export default ProcessFlowEdge;
